import React, { useMemo, useState } from 'react';
import { DashboardMetrics } from './components/DashboardMetrics';
import { StageFilter } from './components/StageFilter';
import { LeadTable } from './components/LeadTable';
import { mockLeads, STAGES } from './data/mockData';
import { LoanStage } from './types';
import { Search, LayoutList, BarChart3 } from 'lucide-react';
export function App() {
  const [activeTab, setActiveTab] = useState<'pipeline' | 'metrics'>('pipeline');
  const [activeStage, setActiveStage] = useState<LoanStage | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  // Calculate counts for the filter bar
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {
      All: mockLeads.length
    };
    STAGES.forEach(stage => {
      counts[stage] = mockLeads.filter(lead => lead.stage === stage).length;
    });
    return counts as Record<LoanStage | 'All', number>;
  }, []);
  // Filter leads based on active stage and search query
  const filteredLeads = useMemo(() => {
    return mockLeads.filter(lead => {
      const matchesStage = activeStage === 'All' || lead.stage === activeStage;
      const matchesSearch = lead.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || lead.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || lead.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStage && matchesSearch;
    });
  }, [activeStage, searchQuery]);
  return <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col" data-id="element-0">
      {/* Main Content */}
      <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col" data-id="element-1">
        {/* Header Area */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-2">
          <div data-id="element-3">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-4">
              {activeTab === 'pipeline' ? 'Pipeline Overview' : 'Performance Metrics'}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-5">
              {activeTab === 'pipeline' ? 'Manage and track all active files across stages.' : 'Track your production numbers and trends over time.'}
            </p>
          </div>
          <div className="flex items-center space-x-3" data-id="element-6">
            {activeTab === 'pipeline' && <div className="relative" data-id="element-7">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8">
                  <Search className="h-4 w-4 text-gray-400" data-id="element-9" />
                </div>
                <input type="text" placeholder="Search leads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full sm:w-64 pl-9 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 sm:text-sm transition-all" data-id="element-10" />
              </div>}
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors whitespace-nowrap" data-id="element-11">
              New Lead
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white border border-gray-200 rounded-lg p-1 mb-6 w-fit shadow-sm" data-id="element-12">
          <button onClick={() => setActiveTab('pipeline')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'pipeline' ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-600/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-13">
            <LayoutList className={`w-4 h-4 mr-2 ${activeTab === 'pipeline' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-14" />
            Pipeline
          </button>
          <button onClick={() => setActiveTab('metrics')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'metrics' ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-600/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-15">
            <BarChart3 className={`w-4 h-4 mr-2 ${activeTab === 'metrics' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-16" />
            Metrics
          </button>
        </div>

        {activeTab === 'pipeline' ? <div className="flex-1 flex flex-col min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-17">
            {/* Stage Filter Bar */}
            <div className="border-b border-gray-200 bg-gray-50/50 px-4 pt-4" data-id="element-18">
              <StageFilter activeStage={activeStage} onStageChange={setActiveStage} stageCounts={stageCounts} data-id="element-19" />
            </div>

            {/* Master Lead Table */}
            <div className="flex-1 flex flex-col min-h-0" data-id="element-20">
              <LeadTable leads={filteredLeads} data-id="element-21" />
            </div>
          </div> : <DashboardMetrics data-id="element-22" />}
      </main>
    </div>;
}